import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FencyContentStatus, SignedDocumentDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { useFency } from "./useFency";
import { parseFencyContentStatus } from "./parseFencyContentStatus";
import { getPusher } from "../pusher";
import { isUnsignedSignedDocument } from "../documents/utils";

export type DocumentSyncStatus = {
  doc: ViewDocumentAsOwnerDto | SignedDocumentDto;
  status: FencyContentStatus | null;
  error: boolean;
  syncing: boolean;
};

export const useFencyBulkDocumentSync = (
  documents: (ViewDocumentAsOwnerDto | SignedDocumentDto)[],
  refetchDocuments?: () => void | Promise<void>
) => {
  const { triggerSync } = useFency();
  const [documentStatuses, setDocumentStatuses] = useState<DocumentSyncStatus[]>(() =>
    documents.map((doc) => ({
      doc,
      status: doc.fencyContentStatus ?? null,
      error: false,
      syncing: false,
    }))
  );

  useEffect(() => {
    setDocumentStatuses((prev) => {
      const byId = new Map(prev.map((p) => [p.doc.id, p]));
      return documents.map((doc) => {
        const existing = byId.get(doc.id);
        return existing ?? { doc, status: doc.fencyContentStatus ?? null, error: false, syncing: false };
      });
    });
  }, [documents]);

  const inFlightDocIds = useRef(new Set<string>());

  const syncDocument = useCallback(
    async (doc: ViewDocumentAsOwnerDto | SignedDocumentDto) => {
      setDocumentStatuses((prev) =>
        prev.map((item) =>
          item.doc.id === doc.id
            ? {
                ...item,
                error: false,
                syncing: true,
                status: item.status === FencyContentStatus.SynchronizationError ? null : item.status,
              }
            : item
        )
      );
      inFlightDocIds.current.add(doc.id);
      try {
        await triggerSync(doc);
        inFlightDocIds.current.delete(doc.id);
        void refetchDocuments?.();
      } catch {
        inFlightDocIds.current.delete(doc.id);
        setDocumentStatuses((prev) =>
          prev.map((item) => (item.doc.id === doc.id ? { ...item, error: true, syncing: false } : item))
        );
      }
    },
    [triggerSync, refetchDocuments]
  );

  const retry = useCallback(
    (docId: string) => {
      const doc = documents.find((d) => d.id === docId);
      if (doc && !isUnsignedSignedDocument(doc)) void syncDocument(doc);
    },
    [documents, syncDocument]
  );

  const requestedDocIds = useRef(new Set<string>());

  useEffect(() => {
    documents.filter(needsSync).forEach((doc) => {
      if (requestedDocIds.current.has(doc.id)) return;
      requestedDocIds.current.add(doc.id);
      void syncDocument(doc);
    });
  }, [documents, syncDocument]);

  const refetchRef = useRef(refetchDocuments);
  refetchRef.current = refetchDocuments;

  const pendingDocIdsKey = useMemo(
    () =>
      documents
        .filter(needsSync)
        .map((doc) => doc.id)
        .join(","),
    [documents]
  );

  useEffect(() => {
    if (!pendingDocIdsKey) return;
    const bindings = pendingDocIdsKey.split(",").map((docId) => {
      const handler = (data: unknown) => {
        const status = parseFencyContentStatus(data);
        if (status) {
          setDocumentStatuses((prev) =>
            prev.map((item) => (item.doc.id === docId ? { ...item, status, error: false, syncing: false } : item))
          );
          void refetchRef.current?.();
        }
      };
      const channel = getPusher().subscribe(docId);
      channel.bind("fency-memory-updated", handler);
      return { channel, handler };
    });

    return () => {
      bindings.forEach(({ channel, handler }) => {
        channel.unbind("fency-memory-updated", handler);
      });
    };
  }, [pendingDocIdsKey]);

  const lastServerStatus = useRef(new Map<string, FencyContentStatus | null>());

  useEffect(() => {
    const newlyFailed = new Set<string>();
    documents.forEach((doc) => {
      const status = doc.fencyContentStatus ?? null;
      const seen = lastServerStatus.current;
      const changed = seen.has(doc.id) && seen.get(doc.id) !== status;
      seen.set(doc.id, status);
      if (changed && status === FencyContentStatus.SynchronizationError && !inFlightDocIds.current.has(doc.id)) {
        newlyFailed.add(doc.id);
      }
    });
    setDocumentStatuses((prev) =>
      prev.map((item) => {
        const doc = documents.find((d) => d.id === item.doc.id);
        if (doc?.fencyContentStatus === FencyContentStatus.Synchronized) {
          return { ...item, status: FencyContentStatus.Synchronized, syncing: false };
        }
        if (newlyFailed.has(item.doc.id)) {
          return { ...item, status: FencyContentStatus.SynchronizationError, error: false, syncing: false };
        }
        return item;
      })
    );
  }, [documents]);

  return { documentStatuses, retry };
};

const needsSync = (doc: ViewDocumentAsOwnerDto | SignedDocumentDto) =>
  doc.fencyContentStatus !== FencyContentStatus.Synchronized && !isUnsignedSignedDocument(doc);
